import clsx from "clsx";
import { Meter as RACMeter } from "react-aria-components";

import {
  categoricalColorVars,
  type CategoricalSlot,
} from "./categorical-color";
import styles from "./meter.module.css";

export type MeterSize = "inline" | "wide";
export type MeterLayout = "row" | "stacked";
/**
 * How the fill reads: `good`, `fair` and `poor` grade a rate; `progress` is
 * work moving toward done and `idle` is work not started. Absent is the
 * default accent.
 */
export type MeterTone = "good" | "fair" | "poor" | "progress" | "idle";

export interface MeterProps {
  /** The numerator; forwarded to RAC `Meter`'s `value`. */
  done: number;
  /** The denominator; forwarded to RAC `Meter`'s `maxValue`. */
  total: number;
  /**
   * `"inline"` (default) — a fixed 82px track, for a table row.
   * `"wide"` — a track that fills its container, for the plan card and a
   * table's wide column.
   */
  size?: MeterSize;
  /**
   * `"row"` (default) — bar and label side by side.
   * `"stacked"` — bar above a right-aligned label, the plan card's geometry.
   */
  layout?: MeterLayout;
  /** Pre-formatted text, e.g. `"3/10 picks"`. Defaults to `${done}/${total}`.
   *  Also used as the meter's accessible name. */
  label?: string;
  /**
   * Colours the fill by what the value *means* (a scan rate is good or bad; a
   * progress count is just progress), so the caller decides the thresholds and
   * pallet only owns the colours. Without it the fill is the accent, turning
   * success at 100%; with it, the tone wins, and the track takes the lighter
   * data-viz track colour.
   */
  tone?: MeterTone;
  /**
   * Colours the fill by *which thing it is* rather than what the value means: a
   * slot of the categorical ramp (see `categoricalSlotByIndex`), for a list of
   * meters that should be told apart, one per zone say. A `tone` wins over it,
   * since that is a statement about the value, and at 100% it does not turn
   * success: a zone's colour is its identity, not its progress.
   */
  slot?: CategoricalSlot;
  /** For a meter on the brand surface (`surface-brand`): light track and label. */
  onBrand?: boolean;
  "data-test-role"?: string;
}

/**
 * Wraps RAC `Meter` — "a quantity within a known range," not `ProgressBar`
 * ("indeterminate/async progress"), per SR-301 phase 8 doc §2c. Retires
 * `tasks/-components/task-bits.tsx`'s hand-rolled `Progress`: RAC's
 * `percentage` render-prop value drives the fill width, so callers stop
 * computing `percent` themselves.
 *
 * The "no data yet" case (`task-table.tsx`'s discriminated union) stays a
 * caller concern — it never reaches `Progress` today and must not start
 * reaching `Meter` either, so there is no "unknown" arm here.
 */
function Meter({
  done,
  total,
  size = "inline",
  layout = "row",
  label,
  tone,
  slot,
  onBrand,
  "data-test-role": testRole,
}: MeterProps) {
  const text = label ?? `${done}/${total}`;
  const isComplete = total > 0 && done >= total;

  return (
    <RACMeter
      value={done}
      maxValue={total}
      aria-label={text}
      className={clsx(styles.meter, {
        [styles.stacked]: layout === "stacked",
        [styles.wide]: size === "wide",
        [styles.onBrand]: onBrand,
      })}
      data-test-role={testRole}
    >
      {({ percentage }) => (
        <>
          <div
            className={clsx(styles.track, {
              [styles.trackWide]: size === "wide",
              [styles.trackToned]: tone !== undefined || slot !== undefined,
            })}
          >
            <div
              className={clsx(styles.fill, {
                [styles.fillComplete]:
                  isComplete && !tone && slot === undefined,
                [styles.toneGood]: tone === "good",
                [styles.toneFair]: tone === "fair",
                [styles.tonePoor]: tone === "poor",
                [styles.toneProgress]: tone === "progress",
                [styles.toneIdle]: tone === "idle",
              })}
              style={{
                width: `${percentage}%`,
                ...(slot !== undefined && tone === undefined
                  ? { backgroundColor: categoricalColorVars(slot).color }
                  : {}),
              }}
            />
          </div>
          <span className={styles.label}>{text}</span>
        </>
      )}
    </RACMeter>
  );
}

export default Meter;
